/*
 * orderRequestItemIgnore.ts
 *
 * Client-side twin of the server's `src/ecommerce/OrderRequest.ts` helpers
 * (`isIgnoredOrderRequestItem` / `activeOrderRequestItems`) in
 * SnowfallPressServerless. Same names, same rule, on purpose.
 *
 * WHY THIS IS A SEPARATE FILE
 * ---------------------------
 * It is the single definition of "is this order line dismissed?" for the
 * client. Three places need it and they must not drift:
 *
 *   1. `deleteItem()` / `restoreItem()`      -- writing the state
 *   2. `updateEcommerceOrderAndComputeIfReady()` -- judging order readiness
 *   3. the delete/restore buttons in the template -- which affordance to show
 *
 * Keeping it out of the component also means it can be exercised by
 * `tests/OrderRequestItemIgnore.smoke.ts` under plain ts-node, without dragging
 * in Angular.
 *
 * THE RULE
 * --------
 *     status === 'IGNORE'  ||  numeric quantity <= 0
 *
 * The second half is not redundant: it is what clears orders that were
 * dismissed before the 'IGNORE' status existed, which carry only the zero
 * quantity and would otherwise stay stuck in `productStatus = 'ERROR'` forever.
 *
 * A missing, blank or unparseable quantity is deliberately NOT an ignore.
 * Those are parsing failures in the inbound order, not operator intent, and
 * swallowing them would turn a loud "item quantities must be greater or equal
 * to 1" into an order that silently ships short.
 *
 * See docs/contracts/inbox/_processed/2026-08-26-order-request-item-ignore-implemented.md
 */

/**
 * `OrderRequestItem.status` value meaning "the operator dismissed this line".
 *
 * Unlike the other values this field carries ('SUCCESS', 'ERROR',
 * 'ERROR_NO_REMOTE_PRODUCT_ID') which are resolution *results* written by the
 * server or by the product editor, this one is client-supplied *intent*.
 */
export const ORDER_REQUEST_ITEM_STATUS_IGNORE = 'IGNORE';

/**
 * True when this order line should be treated as dismissed and left out of
 * pricing, packing, manufacturing and readiness.
 *
 *   0, '0', -2                  -> ignored
 *   1, '5'                      -> active
 *   undefined, null, '', 'two'  -> NOT ignored (falls through to validation)
 */
export function isIgnoredOrderRequestItem(item: any): boolean {
  if ( !item ) {
    return false;
  }
  if ( item.status === ORDER_REQUEST_ITEM_STATUS_IGNORE ) {
    return true;
  }
  const quantity = item.quantity;
  // Checked explicitly: Number('') and Number(null) are both 0, which would
  // otherwise misclassify a parse failure as a deliberate dismissal.
  if ( quantity === undefined || quantity === null || quantity === '' ) {
    return false;
  }
  const numericQuantity = Number(quantity);
  if ( isNaN(numericQuantity) ) {
    return false;
  }
  return numericQuantity <= 0;
}

/**
 * The lines that still count. Returns a new array -- never filters in place,
 * because the dismissed lines, their `initialQuantity` and their status all
 * have to survive for the audit view and the restore affordance.
 */
export function activeOrderRequestItems<T = any>(items: T[] | undefined | null): T[] {
  if ( !items ) {
    return [];
  }
  return items.filter(item => !isIgnoredOrderRequestItem(item));
}

